import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Unsubscribe from "./pages/Unsubscribe";
import { Loader2 } from "lucide-react";
import { BRAND } from "@/brand.config";
import { captureException } from "@/lib/sentry";

const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// Global query/mutation error capture: TanStack Query failures land in
// Sentry with their key attached for triage. Filters out errors we expect
// during normal use (aborts on unmount, transient network, offline) so
// the project doesn't drown in low-signal noise.
function shouldReportQueryError(error: unknown): boolean {
  if (error instanceof Error) {
    // React Query aborts in-flight queries when components unmount; that's
    // not a bug, just a race we explicitly accept.
    if (error.name === "AbortError") return false;
    // Browser fetch network failures (offline, DNS, etc.) — out of our
    // control and would spam Sentry on flaky connections.
    if (error.message === "Failed to fetch") return false;
    if (error.message === "Load failed") return false; // Safari variant
    if (error.message === "NetworkError when attempting to fetch resource.") return false; // Firefox
  }
  return true;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!shouldReportQueryError(error)) return;
      captureException(error, {
        source: "react_query",
        queryKey: query.queryKey,
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (!shouldReportQueryError(error)) return;
      const extra: Record<string, unknown> = { source: "react_query_mutation" };
      // Only include mutationKey if explicitly set — most mutations don't,
      // and "mutationKey: undefined" in Sentry context is just noise.
      if (mutation.options.mutationKey) {
        extra.mutationKey = mutation.options.mutationKey;
      }
      captureException(error, extra);
    },
  }),
});

const AdminFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const AdminShell = ({ children }: { children: React.ReactNode }) => (
  <AuthProvider>
    <Suspense fallback={<AdminFallback />}>{children}</Suspense>
  </AuthProvider>
);

const App = () => {
  // Keep document.title in sync with the configured brand. Vite injects
  // BRAND.pageTitle into index.html at build time; this re-asserts it on
  // mount so dev-server hot reloads and SPA route changes can't desync.
  useEffect(() => {
    document.title = BRAND.pageTitle;
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/admin" element={<AdminShell><Admin /></AdminShell>} />
          <Route path="/admin/dashboard" element={<AdminShell><AdminDashboard /></AdminShell>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
